import type { Unit } from '@/features/interactive-viewer-v2/types/types';

type Point = readonly [number, number];

const MAX_LASSO_POINTS = 72;
const COVERAGE_GRID_SIZE = 10;
export const DEFAULT_AREA_TAG_LASSO_COVERAGE = 0.5;

const pointOnSegment = (point: Point, start: Point, end: Point): boolean => {
  const cross =
    (point[1] - start[1]) * (end[0] - start[0]) - (point[0] - start[0]) * (end[1] - start[1]);
  if (Math.abs(cross) > 1e-8) return false;

  const dot =
    (point[0] - start[0]) * (end[0] - start[0]) + (point[1] - start[1]) * (end[1] - start[1]);
  if (dot < 0) return false;

  const lengthSquared = (end[0] - start[0]) ** 2 + (end[1] - start[1]) ** 2;
  return dot <= lengthSquared;
};

export const pointInPolygonOrBoundary = (point: Point, polygon: readonly Point[]): boolean => {
  if (polygon.length < 3) return false;

  let inside = false;
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
    const currentPoint = polygon[index];
    const previousPoint = polygon[previous];
    if (pointOnSegment(point, previousPoint, currentPoint)) return true;

    const crosses =
      currentPoint[1] > point[1] !== previousPoint[1] > point[1] &&
      point[0] <
        ((previousPoint[0] - currentPoint[0]) * (point[1] - currentPoint[1])) /
          (previousPoint[1] - currentPoint[1] || 1e-12) +
          currentPoint[0];
    if (crosses) inside = !inside;
  }
  return inside;
};

const normalizePolygon = (points: readonly number[][]): Point[] =>
  points
    .filter((point) => point.length === 2 && point.every(Number.isFinite))
    .map((point): Point => [point[0], point[1]]);

const simplifyLasso = (points: readonly Point[]): Point[] => {
  if (points.length <= MAX_LASSO_POINTS) return [...points];
  const stride = Math.ceil(points.length / MAX_LASSO_POINTS);
  const simplified = points.filter((_, index) => index % stride === 0);
  const last = points[points.length - 1];
  if (simplified[simplified.length - 1] !== last) simplified.push(last);
  return simplified;
};

export const getPolygonLassoCoverage = (
  polygonInput: readonly number[][],
  lassoInput: readonly number[][]
): number => {
  const polygon = normalizePolygon(polygonInput);
  const lasso = simplifyLasso(normalizePolygon(lassoInput));
  if (polygon.length < 3 || lasso.length < 3) return 0;

  const xs = polygon.map(([x]) => x);
  const ys = polygon.map(([, y]) => y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const width = maxX - minX;
  const height = maxY - minY;

  if (width <= 0 || height <= 0) return 0;

  let polygonSamples = 0;
  let coveredSamples = 0;
  for (let row = 0; row < COVERAGE_GRID_SIZE; row += 1) {
    for (let column = 0; column < COVERAGE_GRID_SIZE; column += 1) {
      const point: Point = [
        minX + ((column + 0.5) / COVERAGE_GRID_SIZE) * width,
        minY + ((row + 0.5) / COVERAGE_GRID_SIZE) * height,
      ];
      if (!pointInPolygonOrBoundary(point, polygon)) continue;
      polygonSamples += 1;
      if (pointInPolygonOrBoundary(point, lasso)) coveredSamples += 1;
    }
  }

  if (polygonSamples > 0) return coveredSamples / polygonSamples;

  const coveredVertices = polygon.filter((point) => pointInPolygonOrBoundary(point, lasso)).length;
  return coveredVertices / polygon.length;
};

export const selectAreaTagsMostlyInsideLasso = (
  areaTags: readonly Unit[],
  lasso: readonly number[][],
  minimumCoverage = DEFAULT_AREA_TAG_LASSO_COVERAGE
): Unit[] => {
  if (lasso.length < 3) return [];

  return areaTags.filter(
    (areaTag) => getPolygonLassoCoverage(areaTag.points, lasso) >= minimumCoverage
  );
};
